import React from "react";
import cx from "classnames";

interface MainContainerType {
  children: React.ReactNode;
  rightColumnChildren?: React.ReactNode;
  applySidePadding?: boolean;
  rightColumnExpanded?: boolean;
}

const InnerContainerId = "main-container-inner";
const AppBodyId = "app-body";

export const GetMainScrollContainer = () =>
  document.getElementById(InnerContainerId) ??
  document.getElementById(AppBodyId);

export const ScrollPageMainContainerToTop = () => {
  const container = GetMainScrollContainer();
  if (container) {
    container.scrollTo({ top: 0, behavior: "smooth" });
  }
};

/** Scroll the app shell's inner pane so an element is visible without touching the document. */
export const ScrollMainContainerToElement = (
  element: HTMLElement | null | undefined,
  block: "start" | "nearest" = "start",
) => {
  if (!element) return;

  const container = GetMainScrollContainer();
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const offsetWithinContainer =
    elementRect.top - containerRect.top + container.scrollTop;

  let targetTop = offsetWithinContainer;
  if (block === "nearest") {
    const elementBottom = offsetWithinContainer + elementRect.height;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;
    if (offsetWithinContainer >= viewTop && elementBottom <= viewBottom) {
      return;
    }
    targetTop =
      offsetWithinContainer < viewTop
        ? offsetWithinContainer
        : elementBottom - container.clientHeight;
  }

  container.scrollTo({
    top: Math.max(0, targetTop),
    behavior: "smooth",
  });
};

const MainContainer = ({
  children,
  rightColumnChildren,
  applySidePadding = true,
  rightColumnExpanded = false,
}: MainContainerType) => {
  const leftContainerWidthClassNames = rightColumnChildren
    ? rightColumnExpanded
      ? "lg:w-1/2"
      : "lg:w-3/4"
    : "lg:w-full";

  const rightColumnWidthClassName = rightColumnExpanded
    ? "lg:w-1/2"
    : "lg:w-1/3";

  return (
    <div
      id="main-container"
      className={cx(
        "flex flex-col overflow-auto overflow-x-hidden lg:overflow-hidden max-h-screen lg:max-h-full h-full",
        rightColumnChildren ? "lg:flex-row" : "",
        "print:overflow-visible print:max-h-none print:h-auto",
      )}
    >
      <div
        id={InnerContainerId}
        className={cx(
          "min-w-0 lg:overflow-auto lg:overflow-x-hidden lg:max-h-screen lg:pb-24",
          rightColumnChildren ? "" : "pb-36",
          leftContainerWidthClassNames,
          applySidePadding ? "px-2 md:px-4 lg:px-6" : "",
          "print:overflow-visible",
        )}
      >
        {children}
      </div>
      {rightColumnChildren && (
        <div
          id="right-bar-container"
          className={`${rightColumnWidthClassName} p-4 bg-gray-100/60 lg:max-h-screen lg:overflow-y-auto`}
        >
          {/* Using CSS calc for height to use 100% of the viewport height minus the height of the top bar */}
          {rightColumnChildren}
        </div>
      )}
    </div>
  );
};

export default MainContainer;
